import { Util, Request, XtLocale, Cookie } from "@xt/xtbase";

const { getOrigin } = Util;
const { XtAxios } = Request;

const XT_APP_BASE = `${getOrigin("www")}/xt-app`;

/** 与站点/其它端可能写入的 localStorage key 对齐，按顺序尝试 */
const LANG_LOCAL_STORAGE_KEYS = [
  "lang",
  "locale",
  "i18nextLng",
  "NEXT_LOCALE",
  "language",
];

function parseMaybeJsonLang(raw) {
  const t = String(raw).trim();
  if (!t) return "";
  if (t.startsWith("{")) {
    try {
      const o = JSON.parse(t);
      if (o && typeof o.lang === "string" && o.lang.trim()) return o.lang.trim();
      if (o && typeof o.locale === "string" && o.locale.trim()) return o.locale.trim();
    } catch (_) {
      return "";
    }
  }
  return t.replace(/^["']|["']$/g, "");
}

/**
 * 从浏览器 localStorage 读取语言标识（读不到返回空串）。
 */
export function readLangFromLocalStorage() {
  if (typeof window === "undefined" || !window.localStorage) return "";
  try {
    for (const key of LANG_LOCAL_STORAGE_KEYS) {
      const raw = window.localStorage.getItem(key);
      if (raw == null) continue;
      const v = parseMaybeJsonLang(raw);
      if (v) return v;
    }
  } catch (_) {
    return "";
  }
  return "";
}

/**
 * 归一为学院/公告列表接口 Header `lang`：支持 en、cn、ar、pt、vi；读不到或无法识别时 `en`。
 */
export function normalizeAcademyListLang(raw) {
  if (raw == null) return "en";
  const s = String(raw).trim().toLowerCase();
  if (!s) return "en";
  if (s === "default") return "en";
  if (s === "cn" || s === "zh-cn" || s === "zh_cn") return "cn";
  if (s === "hk" || s === "zh-hk" || s === "zh_hk") return "cn";
  if (s === "ar") return "ar";
  if (s === "pt") return "pt";
  if (s === "vi" || s.startsWith("vi-")) return "vi";
  if (s === "en") return "en";
  return "en";
}

/**
 * 优先 localStorage → Cookie `lang` → 路由 getLangCode → intl.locale，均无时为 `en`。
 */
export function academyConfigListLang() {
  const fromLs = readLangFromLocalStorage();
  const fromCookie =
    typeof Cookie.get === "function" ? Cookie.get("lang") || "" : "";
  const pathCode =
    typeof XtLocale.getLangCode === "function" ? XtLocale.getLangCode() || "" : "";
  const intlLoc = (XtLocale.intl && XtLocale.intl.locale) || "";
  const raw = fromLs || fromCookie || pathCode || intlLoc || "";
  return normalizeAcademyListLang(raw);
}

export function getAcademyConfigList(
  { configType, platform = "web" },
  axiosConfig = {}
) {
  const tenantId =
    (axiosConfig.headers && axiosConfig.headers["tenant-id"]) ||
    (typeof process !== "undefined" &&
      process.env &&
      process.env.NEXT_PUBLIC_TENANT_ID) ||
    "1";
  const url = `${XT_APP_BASE}/public/academy-config/list`;
  return XtAxios.get(url, {
    ...axiosConfig,
    params: {
      ...(axiosConfig.params || {}),
      configType,
      platform,
    },
    headers: {
      ...(axiosConfig.headers || {}),
      lang: academyConfigListLang(),
      "tenant-id": String(tenantId),
    },
  });
}

/**
 * 将接口里的 icon / coverImage 等路径转为可请求的绝对地址（相对路径拼 www 源站）。
 */
export function resolveAcademyConfigAssetUrl(src) {
  if (src == null || typeof src !== "string") return "";
  const t = src.trim();
  if (!t) return "";
  if (/^https?:\/\//i.test(t)) return t;
  if (t.startsWith("//")) return `https:${t}`;
  if (t.startsWith("/")) return `${getOrigin("www")}${t}`;
  return t;
}

/**
 * 列表项跳转地址：linkType 1 用 linkUrl；linkType 2 优先 linkUrl，否则 content 为 http(s) 时作外链。
 * 相对路径 `/...` 会补全为 www 域名（与静态资源路径一致）。
 */
export function resolveAcademyConfigItemLink(row) {
  if (!row) return null;
  const linkType = Number(row.linkType);
  const pick = (v) => {
    if (v == null || typeof v !== "string") return "";
    return v.trim();
  };
  let href = pick(row.linkUrl);
  if (!href && linkType === 2) href = pick(row.content);
  if (!href) return null;
  if (/^https?:\/\//i.test(href)) return href;
  if (href.startsWith("//")) return `https:${href}`;
  if (href.startsWith("/")) return `${getOrigin("www")}${href}`;
  return href;
}

export async function fetchAcademyConfigListParsed({
  configType,
  platform = "web",
} = {}) {
  try {
    const data = await getAcademyConfigList({ configType, platform });
    return Array.isArray(data) ? data : [];
  } catch (e) {
    console.error("academy-config list error", e);
    return [];
  }
}
